'use client';

import { ReactNode, useState } from 'react';
import Image from 'next/image';

import { ButtonV1 } from '@northladder/design-system';
import { Text, useTranslations } from '@northladder/i18n';
import { TTextLocalized } from '@northladder/utilities';

import { CountDownTimer } from '../CountDownTimer';
import { FeedbackMessage, IFeedbackMessageOptions } from '../FeedbackMessage';

interface IUserActionRequestPromptProps {
  hasVisibleTimer?: boolean;
  /**
   * The primary CTA button to be displayed for the main user action.
   */
  buttonCTA?: {
    label: TTextLocalized;
    labelLoading?: TTextLocalized;
    onClick: () => void;
  };
  classNameTextContainer?: string;
  /**
   * Renders a custom component for the parent to control such as a modal CTA.
   */
  customCTA?: ReactNode;
  /**
   * Duration over which the count down timer is displayed before it times out.
   * Default is 20 seconds.
   */
  duration?: number;
  feedbackOptions?: IFeedbackMessageOptions;
  heroImageComponent?: ReactNode;
  imgSrc?: {
    /**
     * The alt text for the hero image to be displayed on the top of the screen.
     */
    alt: string;
    /**
     * The URL of the hero image to be displayed on the top of the screen.
     * If not provided, the default hero image component is displayed.
     */
    url: string;
  };
  onTimeout: () => void;
  onNavBack?: () => void;
  subTitle?: TTextLocalized;
  title?: TTextLocalized;
}

/**
 * -----------------------------------------------------------------------------
 * Provides a scaffold UI the request for user interaction such as accepting
 * some permission access request.
 * Has a a hero icon/image/countdown timer and a CTA button.
 */
export function UserActionRequestPrompt({
  feedbackOptions,
  buttonCTA,
  classNameTextContainer,
  customCTA,
  duration = 20,
  hasVisibleTimer = false,
  heroImageComponent,
  imgSrc,
  onTimeout,
  onNavBack,
  subTitle,
  title,
}: IUserActionRequestPromptProps) {
  const t = useTranslations('buttons');

  const [isLoading, setIsLoading] = useState(false);

  const handleOnClickBtnCTA = () => {
    if (!buttonCTA) return;

    buttonCTA.onClick();

    if (buttonCTA.labelLoading) {
      setIsLoading(true);
    }
  };

  return (
    <div
      className="
        to-primary-100 dark:to-primary-950/50 relative flex h-screen w-full flex-col items-center
        justify-center gap-4 overflow-hidden bg-gradient-to-b
        from-slate-50 dark:from-gray-800
      "
    >
      {feedbackOptions ? <FeedbackMessage {...feedbackOptions} /> : null}
      <div
        className="
          border-primary-50 bg-primary-300/50 relative flex h-56 w-56 items-center
          justify-center overflow-hidden rounded-full border-8
        "
      >
        {heroImageComponent}
        {imgSrc ? (
          <Image alt={imgSrc.alt} height={120} src={imgSrc.url} width={120} />
        ) : null}
      </div>

      <div
        className={`flex max-w-[320px] flex-col gap-2 px-4 pt-24 ${classNameTextContainer}`}
      >
        {title ? (
          <h1 className="text-body-text text-center text-2xl font-bold leading-8">
            <Text text={title} />
          </h1>
        ) : null}

        {subTitle ? (
          <p className="text-body-text text-center text-base font-medium leading-tight">
            <Text text={subTitle} />
          </p>
        ) : null}
      </div>

      {buttonCTA ? (
        <ButtonV1
          className="z-50 mx-auto mt-2 w-auto"
          isLoading={isLoading}
          label={buttonCTA.label}
          loadingLabel={buttonCTA.labelLoading || t('pleaseWait')}
          onClick={handleOnClickBtnCTA}
          type="button"
        />
      ) : null}

      {customCTA}

      {onNavBack ? (
        <button
          className="
            text-primary-600 hover:text-primary-700 dark:text-primary-400
            dark:hover:text-primary-300 p-4 text-sm
          "
          onClick={onNavBack}
          type="button"
        >
          {t('back')}
        </button>
      ) : null}

      <CountDownTimer
        duration={duration}
        isVisible={hasVisibleTimer}
        onTimeout={onTimeout}
      />
    </div>
  );
}
